import { useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { useEffect } from "react";

export default function CartPage() {
  const { isAuthed, loading, openAuth } = useAuth();
  const { items, grandTotal, remove } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthed) { openAuth("login", () => nav("/dashboard/cart")); nav("/", { replace: true }); }
  }, [loading, isAuthed, openAuth, nav]);

  if (!isAuthed) return <div className="min-h-screen bg-background"><Header /><div className="pt-32 text-center text-sm text-muted-foreground">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Checkout</div>
          <h1 className="font-serif text-4xl md:text-5xl text-ink mt-2" data-testid="cart-title">Your cart</h1>

          {items.length === 0 ? (
            <div data-testid="cart-empty" className="py-24 text-center max-w-md mx-auto">
              <ShoppingBag className="h-8 w-8 mx-auto text-muted-foreground" />
              <div className="font-serif text-2xl text-ink mt-4">Your cart is empty.</div>
              <p className="text-muted-foreground mt-2 text-sm">Add photos from any asset page.</p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
              <div className="border border-border bg-white divide-y divide-border" data-testid="cart-items">
                {items.map((it) => (
                  <div key={it.id} data-testid={`cart-item-${it.id}`} className="p-4 flex items-center gap-4">
                    <img src={it.photo?.src?.small || it.photo?.src?.tiny} alt="" className="h-20 w-28 object-cover shrink-0" style={{ backgroundColor: it.photo?.avg_color || "#eee" }} />
                    <div className="flex-1 min-w-0">
                      <button onClick={() => nav(`/dashboard/photo/${it.photo_id}`)} className="text-sm text-ink hover:text-gold truncate block text-left">
                        {it.photo?.alt || `Asset ${it.photo_id}`}
                      </button>
                      <div className="text-xs text-muted-foreground mt-1">
                        {it.size} · {it.license} · {it.use} · {it.pack} · ${it.unit_price} × {it.pack_count}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground mt-1">ID {it.photo_id}</div>
                    </div>
                    <div className="text-lg text-ink font-serif shrink-0">${it.total}</div>
                    <button onClick={() => remove(it.id)} data-testid={`cart-remove-${it.id}`} className="h-9 w-9 inline-flex items-center justify-center hover:bg-secondary text-destructive shrink-0" title="Remove">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              <aside className="border border-ink bg-white p-6 h-fit" data-testid="cart-summary">
                <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Order summary</div>
                <div className="mt-4 flex items-baseline justify-between">
                  <div className="text-sm text-ink">Grand total</div>
                  <div className="font-serif text-3xl text-ink" data-testid="cart-grand-total">${grandTotal}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{items.length} item{items.length === 1 ? "" : "s"}</div>
                <button disabled data-testid="checkout-btn" className="w-full mt-6 px-5 py-3 bg-ink text-ivory opacity-60 cursor-not-allowed text-sm tracking-wide">
                  Checkout — Coming soon
                </button>
                <div className="text-[11px] text-muted-foreground text-center mt-2">Payment integration is not yet available.</div>
              </aside>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
